import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";
import React from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  FileUpload,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Separator,
} from "@/components";
import { Combobox } from "@/components/ui/combobox";
import { FloatingLabelInput } from "@/components/ui/floating-label-input";
import { useToast } from "@/components/ui/use-toast";
import { config } from "@/config/config";
import { useI18n } from "@/context/i18n";
import { formatCpfCnpj } from "@/core/utils";

import * as projectsApi from "../projects/projects.api";
import * as clientsApi from "./clients.api";
import { useClients } from "./clients.hooks";
import { TClientPreRegisterFormData } from "./clients.schema";

export const ClientRegisterPage: React.FC = () => {
  const { affiliateId } = useParams<{ affiliateId: string }>();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get("projectId");
  const level = searchParams.get("level");
  const fatherOrganizationId = searchParams.get("fatherOrganizationId");
  const organizationId = searchParams.get("organizationId"); // ID da organização criadora (para nível 1)
  
  const { t } = useI18n();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { clientForm, handleClientFormSubmit, handleAddressBlur } = useClients();
  const [isSearching, setIsSearching] = React.useState(false);
  const [foundOrganization, setFoundOrganization] = React.useState<any>(null);

  // Obter organization_id do usuário logado
  const getLoggedUserOrganizationId = React.useCallback(() => {
    try {
      const authUserCookie = Cookies.get("authUser");
      if (authUserCookie) {
        const userData = JSON.parse(authUserCookie);
        return userData?.organization_root_id || null;
      }
    } catch (error) {
      console.error("Erro ao ler cookie authUser:", error);
    }
    return null;
  }, []);

  // Buscar detalhes do projeto para obter organization_father se necessário
  const { data: project } = useQuery({
    queryKey: ["project", projectId],
    queryFn: () => projectId ? projectsApi.getProjectById(projectId) : null,
    enabled: !!projectId,
    staleTime: 60_000,
  });
  
  // Quando for cadastro para projeto, limpar erro de type_partner se existir
  React.useEffect(() => {
    if (projectId && clientForm.formState.errors.type_partner) {
      clientForm.clearErrors("type_partner");
    }
  }, [projectId, clientForm]);

  const businessClassificationOptions = [
    { value: "comercio", label: t("clients.business_classification.comercial") },
    { value: "industria", label: t("clients.business_classification.industria") },
    { value: "servicos", label: t("clients.business_classification.servicos") },
  ];

  // Buscar organização por CNPJ
  const handleCnpjSearch = React.useCallback(
    async (cnpj: string) => {
      const cleanCnpj = cnpj.replace(/\D/g, "");
      if (cleanCnpj.length !== 14) {
        setFoundOrganization(null);
        return;
      }

      setIsSearching(true);
      try {
        // Buscar por CNPJ usando a rota /organizations/cnpj/:cnpj
        const result = await projectsApi.findOrganizationByCnpj(cleanCnpj);
        if (result) {
          // Organização encontrada - mostrar opção para associar ao projeto
          setFoundOrganization(result);
        } else {
          setFoundOrganization(null);
        }
      } catch (error: any) {
        // Se não encontrar, não é erro - apenas não existe ainda
        if (error?.response?.status !== 404) {
          console.error("Erro ao buscar organização:", error);
        }
        setFoundOrganization(null);
      } finally {
        setIsSearching(false);
      }
    },
    [toast, t]
  );

  // Associar organização existente ao projeto
  const handleAssociateExistingOrganization = React.useCallback(
    async (e?: React.MouseEvent) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      if (!foundOrganization) {
        toast({
          title: t("generic.error"),
          description: "Organização não encontrada",
          variant: "destructive",
        });
        return;
      }

      if (!projectId) {
        toast({
          title: t("generic.error"),
          description: "ID do projeto não encontrado",
          variant: "destructive",
        });
        return;
      }

      if (!level) {
        toast({
          title: t("generic.error"),
          description: "Nível da hierarquia não encontrado",
          variant: "destructive",
        });
        return;
      }

      // Verificar se o projeto foi carregado (necessário para nível 1)
      const levelNumber = parseInt(level);
      if (levelNumber === 1 && !project) {
        toast({
          title: t("generic.error"),
          description: "Carregando informações do projeto. Aguarde um momento e tente novamente.",
          variant: "destructive",
        });
        return;
      }

      // Determinar o fatherOrganizationId baseado no nível:
      // - Nível 1: usa o organization_father do projeto (criador do projeto)
      // - Nível 2+: usa o fatherOrganizationId da URL (organização pai do nível anterior)
      let finalFatherOrganizationId: string | null = null;

      if (levelNumber === 1) {
        // Nível 1: pai é o criador do projeto
        // Prioridade: organizationId da URL > organization_father do projeto > organization_id do projeto > organization_id do usuário logado
        const loggedUserOrgId = getLoggedUserOrganizationId();
        finalFatherOrganizationId = organizationId || project?.organization_father?.id || project?.organization_id || loggedUserOrgId || null;
        
        if (!finalFatherOrganizationId) {
          console.error("Projeto não tem organization_father nem organization_id. Dados do projeto:", {
            project_id: project?.project_id,
            project_name: project?.project_name,
            organization_id: project?.organization_id,
            organization_father: project?.organization_father,
            partner: project?.partner,
            organizationId_from_url: organizationId,
            loggedUserOrgId: loggedUserOrgId,
          });
          
          toast({
            title: t("generic.error"),
            description: "Organização criadora do projeto não encontrada. Verifique se você está logado e se o projeto foi criado corretamente.",
            variant: "destructive",
          });
          return;
        }
      } else {
        // Nível 2+: pai é o fatherOrganizationId da URL
        finalFatherOrganizationId = fatherOrganizationId || null;
        
        if (!finalFatherOrganizationId) {
          toast({
            title: t("generic.error"),
            description: "ID da organização pai não encontrado na URL. Para níveis acima de 1, é necessário informar a organização pai.",
            variant: "destructive",
          });
          return;
        }
      }

      try {
        const organizationId = foundOrganization.id || foundOrganization.organization_id || foundOrganization._id;
        
        if (!organizationId) {
          toast({
            title: t("generic.error"),
            description: "ID da organização não encontrado",
            variant: "destructive",
          });
          return;
        }

        await projectsApi.associateExistingOrganizationToProject(
          projectId,
          finalFatherOrganizationId,
          organizationId,
          level,
          foundOrganization // Passar os dados completos da organização encontrada
        );

        // Invalidar a query do projeto para recarregar os dados quando o usuário voltar
        queryClient.invalidateQueries({ queryKey: ["project", projectId] });

        toast({
          title: t("generic.success"),
          description: t("clients.register.organizationAssociated"),
        });

        // Limpar estado
        setFoundOrganization(null);
        
        // Não redirecionar automaticamente - deixar o usuário decidir
        // A organização já foi associada e aparecerá nos detalhes do projeto
      } catch (error: any) {
        console.error("Erro ao associar organização:", error);
        toast({
          title: t("generic.error"),
          description: error?.response?.data?.message || error?.message || t("clients.register.errorAssociating"),
          variant: "destructive",
        });
      }
    },
    [foundOrganization, projectId, fatherOrganizationId, level, project, organizationId, getLoggedUserOrganizationId, toast, t, queryClient]
  );

  const handleSubmit = React.useCallback(
    async (data: TClientPreRegisterFormData) => {
      // Se for cadastro para projeto, limpar erro de type_partner antes de validar
      if (projectId) {
        clientForm.clearErrors("type_partner");
      }
      
      try {
        // Se tiver projectId, criar organização e associar ao projeto
        if (projectId && fatherOrganizationId && level) {
          const formData = new FormData();

          // Preparar dados JSON
          const jsonData = {
            name: data.name,
            cnpj: data.cnpj.replace(/\D/g, ""),
            corporate_name: data.corporate_name,
            email: data.email,
            business_classification: data.business_classification,
            address: {
              postal_code: data.address.postal_code.replace(/\D/g, ""),
              street: data.address.street,
              number: data.address.number,
              complement: data.address.complement || "",
              neighborhood: data.address.neighborhood,
              city: data.address.city,
              state: data.address.state,
            },
            company_representative: {
              first_name: data.company_representative.first_name,
              last_name: data.company_representative.last_name,
              personal_email: data.company_representative.personal_email,
              cpf: data.company_representative.cpf.replace(/\D/g, ""),
              mobile_phone: data.company_representative.mobile_phone.replace(/\D/g, ""),
            },
            bank_account: {
              bank_name: data.bank_account.bank_name,
              bank_branch: data.bank_account.bank_branch,
              checking_account: data.bank_account.checking_account,
              checking_account_digit: data.bank_account.checking_account_digit,
              pix_key: data.bank_account.pix_key?.trim() ?? "",
            },
            signed_terms: {
              signed: true,
              signed_at: new Date().toISOString(),
              ip_address: "127.0.0.1", // TODO: Obter IP real
            },
          };

          formData.append("data", JSON.stringify(jsonData));

          // Adicionar arquivos
          if (data.document.company_logo) {
            formData.append("document.company_logo", data.document.company_logo);
          }
          if (data.document.cnpj_card) {
            formData.append("document.cnpj_card", data.document.cnpj_card);
          }
          if (data.document.partners_documents_cpf) {
            data.document.partners_documents_cpf.forEach((file) => {
              formData.append("document.partners_documents_cpf", file);
            });
          }
          if (data.document.articles_of_incorporation) {
            data.document.articles_of_incorporation.forEach((file) => {
              formData.append("document.articles_of_incorporation", file);
            });
          }
          if (data.document.signed_contract) {
            data.document.signed_contract.forEach((file) => {
              formData.append("document.contract_signed", file);
            });
          }

          await projectsApi.createOrganizationForProject(
            projectId,
            fatherOrganizationId,
            formData
          );

          toast({
            title: t("generic.success"),
            description: "Organização cadastrada e associada ao projeto com sucesso!",
          });
        } else {
          // Cadastro normal (sem projeto)
          handleClientFormSubmit(data);
          toast({
            title: t("generic.success"),
            description: t("clients.success.clientCreated"),
          });
        }
      } catch (error: any) {
        toast({
          title: t("generic.error"),
          description: error.message || "Erro ao cadastrar organização",
          variant: "destructive",
        });
      }
    },
    [projectId, fatherOrganizationId, level, handleClientFormSubmit, toast, t]
  );

  return (
    <div className="container mx-auto py-8 px-4">
      <Card className="max-w-4xl mx-auto">
        <CardHeader className="text-center">
          <div className="flex justify-center items-center py-4">
            <img src={config.logoUrl} alt={config.brandName} className="w-24 h-12" />
          </div>
          <CardTitle className="text-2xl font-bold text-red-600">
            {t("clients.register.title")}
          </CardTitle>
          <CardDescription className="text-gray-600 space-y-2 mt-4">
            <p>{t("clients.register.description")}</p>
            {projectId && level && (
              <p className="text-sm font-semibold text-primary">
                Cadastro para projeto - Nível {level}
              </p>
            )}
          </CardDescription>
        </CardHeader>

        <CardContent>
          <Form {...clientForm}>
            <form onSubmit={clientForm.handleSubmit(handleSubmit)} className="space-y-8">
              {/* Busca por CNPJ */}
              {projectId && (
                <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold">{t("clients.register.searchExistingOrganization")}</h4>
                  </div>
                  <div className="flex gap-2">
                    <FloatingLabelInput
                      label={t("clients.form.fields.cnpj")}
                      placeholder="00.000.000/0000-00"
                      onBlur={(e) => {
                        const cnpj = e.target.value.replace(/\D/g, "");
                        if (cnpj.length === 14) {
                          handleCnpjSearch(cnpj);
                        } else {
                          setFoundOrganization(null);
                        }
                      }}
                      onChange={(e) => {
                        const { value } = e.target;
                        e.target.value = formatCpfCnpj(value);
                        // Limpar organização encontrada quando o CNPJ mudar
                        if (foundOrganization) {
                          setFoundOrganization(null);
                        }
                      }}
                      disabled={isSearching}
                    />
                    {isSearching && (
                      <div className="flex items-center text-sm text-muted-foreground">
                        {t("generic.loading")}
                      </div>
                    )}
                  </div>
                  
                  {/* Mensagem quando organização for encontrada */}
                  {foundOrganization && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <div className="flex-1">
                          <p className="font-semibold text-green-800 mb-1">
                            {t("clients.register.organizationFound")}
                          </p>
                          <p className="text-sm text-green-700 mb-3">
                            {foundOrganization.name} - {foundOrganization.cnpj}
                          </p>
                          <Button
                            type="button"
                            onClick={handleAssociateExistingOrganization}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            {t("clients.register.associateToProject")}
                          </Button>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setFoundOrganization(null)}
                          className="text-green-700 hover:text-green-800"
                        >
                          {t("generic.close")}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <Separator />

              {/* Seção: Dados da Empresa */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-8 bg-primary rounded-full"></div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {t("clients.form.sections.companyData")}
                  </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <FormField
                    control={clientForm.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <FloatingLabelInput
                            label={`${t("clients.form.fields.name")} *`}
                            placeholder=" "
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={clientForm.control}
                    name="cnpj"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <FloatingLabelInput
                            label={`${t("clients.form.fields.cnpj")} *`}
                            placeholder=" "
                            {...field}
                            onChange={(e) => {
                              const { value } = e.target;
                              e.target.value = formatCpfCnpj(value);
                              field.onChange(e);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={clientForm.control}
                    name="corporate_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <FloatingLabelInput
                            label={`${t("clients.form.fields.corporate_name")} *`}
                            placeholder=" "
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={clientForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <FloatingLabelInput
                            label={`${t("clients.form.fields.email")} *`}
                            placeholder=" "
                            type="email"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={clientForm.control}
                    name="business_classification"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Combobox
                            label={`${t("clients.form.fields.business_classification")} *`}
                            placeholder={t("clients.form.fields.business_classification")}
                            options={businessClassificationOptions}
                            value={field.value}
                            onChange={field.onChange}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <Separator />

              {/* Seção: Endereço */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-8 bg-primary rounded-full"></div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {t("clients.form.sections.address")}
                  </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <FormField
                    control={clientForm.control}
                    name="address.postal_code"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <FloatingLabelInput
                            label={`${t("clients.form.fields.postal_code")} *`}
                            placeholder=" "
                            {...field}
                            onBlur={handleAddressBlur}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={clientForm.control}
                    name="address.street"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <FloatingLabelInput
                            label={`${t("clients.form.fields.street")} *`}
                            placeholder=" "
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={clientForm.control}
                    name="address.number"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <FloatingLabelInput
                            label={`${t("clients.form.fields.number")} *`}
                            placeholder=" "
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={clientForm.control}
                    name="address.complement"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <FloatingLabelInput
                            label={t("clients.form.fields.complement")}
                            placeholder=" "
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={clientForm.control}
                    name="address.neighborhood"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <FloatingLabelInput
                            label={`${t("clients.form.fields.neighborhood")} *`}
                            placeholder=" "
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={clientForm.control}
                    name="address.city"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <FloatingLabelInput
                            label={`${t("clients.form.fields.city")} *`}
                            placeholder=" "
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={clientForm.control}
                    name="address.state"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <FloatingLabelInput
                            label={`${t("clients.form.fields.state")} *`}
                            placeholder=" "
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <Separator />

              {/* Seção: Representante da Empresa */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-8 bg-primary rounded-full"></div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {t("clients.form.sections.representative")}
                  </h3>
                </div>
                <p className="text-sm text-gray-500">
                  {t("clients.form.sections.representative.description")}
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <FormField
                    control={clientForm.control}
                    name="company_representative.first_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <FloatingLabelInput
                            label={`${t("clients.form.fields.first_name")} *`}
                            placeholder=" "
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={clientForm.control}
                    name="company_representative.last_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <FloatingLabelInput
                            label={`${t("clients.form.fields.last_name")} *`}
                            placeholder=" "
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={clientForm.control}
                    name="company_representative.personal_email"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <FloatingLabelInput
                            label={`${t("clients.form.fields.personal_email")} *`}
                            placeholder=" "
                            type="email"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={clientForm.control}
                    name="company_representative.cpf"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <FloatingLabelInput
                            label={`${t("clients.form.fields.cpf")} *`}
                            placeholder=" "
                            {...field}
                            onChange={(e) => {
                              const { value } = e.target;
                              e.target.value = formatCpfCnpj(value);
                              field.onChange(e);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={clientForm.control}
                    name="company_representative.mobile_phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <FloatingLabelInput
                            label={`${t("clients.form.fields.mobile_phone")} *`}
                            placeholder=" "
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <Separator />

              {/* Seção: Dados Bancários */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-8 bg-primary rounded-full"></div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {t("clients.form.sections.bankAccount")}
                  </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <FormField
                    control={clientForm.control}
                    name="bank_account.bank_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <FloatingLabelInput
                            label={`${t("clients.form.fields.bank_name")} *`}
                            placeholder=" "
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={clientForm.control}
                    name="bank_account.bank_branch"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <FloatingLabelInput
                            label={`${t("clients.form.fields.bank_branch")} *`}
                            placeholder=" "
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={clientForm.control}
                    name="bank_account.checking_account"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <FloatingLabelInput
                            label={`${t("clients.form.fields.checking_account")} *`}
                            placeholder=" "
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={clientForm.control}
                    name="bank_account.checking_account_digit"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <FloatingLabelInput
                            label={`${t("clients.form.fields.checking_account_digit")} *`}
                            placeholder=" "
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={clientForm.control}
                  name="bank_account.pix_key"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <FloatingLabelInput
                          label={t("clients.form.fields.pix_key")}
                          placeholder={t("clients.form.placeholders.pix_key")}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <Separator />

              {/* Seção: Documentos */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-8 bg-primary rounded-full"></div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {t("clients.form.sections.documents")}
                  </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <FormField
                    control={clientForm.control}
                    name="document.company_logo"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <FileUpload
                            label={`${t("clients.form.fields.company_logo")} *`}
                            description={t("clients.form.fields.company_logo_description")}
                            accept="image/*"
                            value={field.value as File[]}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            name={field.name}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={clientForm.control}
                    name="document.cnpj_card"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <FileUpload
                            label={`${t("clients.form.fields.cnpj_card")} *`}
                            description={t("clients.form.fields.cnpj_card_description")}
                            accept=".pdf,.jpg,.jpeg,.png"
                            value={field.value as File[]}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            name={field.name}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={clientForm.control}
                    name="document.partners_documents_cpf"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <FileUpload
                            label={`${t("clients.form.fields.partners_documents_cpf")} *`}
                            description={t("clients.form.fields.partners_documents_cpf_description")}
                            accept=".pdf,.jpg,.jpeg,.png"
                            value={field.value as File[]}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            name={field.name}
                            multiple
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={clientForm.control}
                    name="document.articles_of_incorporation"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <FileUpload
                            label={`${t("clients.form.fields.articles_of_incorporation")} *`}
                            description={t("clients.form.fields.articles_of_incorporation_description")}
                            accept=".pdf"
                            value={field.value as File[]}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            name={field.name}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={clientForm.control}
                    name="document.signed_contract"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <FileUpload
                            label={`${t("clients.form.fields.signed_contract")} *`}
                            description={t("clients.form.fields.signed_contract_description")}
                            accept=".pdf,.jpg,.jpeg,.png"
                            value={field.value as File[]}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            name={field.name}
                            multiple
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <Separator />

              {/* Termos de Uso */}
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="terms"
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <label htmlFor="terms" className="text-sm font-medium leading-none">
                  {t("clients.register.terms.accept")}{" "}
                  <a
                    href="https://cdn-dev.soulhub.tec.br/termos/termos_de_uso_v1.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline text-primary"
                  >
                    {t("clients.register.terms.link")}
                  </a>
                </label>
              </div>

              <div className="flex justify-center pt-6">
                <Button type="submit" className="w-full max-w-md" disabled={clientForm.formState.isSubmitting}>
                  {clientForm.formState.isSubmitting ? t("clients.register.submitting") : t("clients.register.submit")}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
};
